/**
 * Quick Test Service
 *
 * Handles execution of Quick Tests with automatic resource allocation.
 * Provides executeOnce() and executeAcrossCluster() functions.
 */

import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { executeFlow } from './runtime';
import { edgeDeviceService } from './edgeDeviceService';
import { getFlow } from './flowService';
import type { QuickTestType } from '../components/quick-tests/QuickTestButton';
import type { ESimProfile } from '@simcheck/shared-types';
import type { CentralEdgeDevice } from '@simcheck/shared-types';

// ============================================================================
// TYPES
// ============================================================================

export interface QuickTestResources {
  eSimProfileId: string;
  locationId: string;
}

// AutoStartConfig includes deviceId/modemImei only for Test Like A Mobile
// (which needs a direct WebSocket connection to a specific modem).
// All other tests let the backend GlobalResourceManager allocate.
export interface AutoStartConfig extends QuickTestResources {
  deviceId: string;
  modemImei: string;
}

export interface ExecuteOnceResult {
  executionId: string;
  autoStartConfig?: AutoStartConfig; // For Test Like A Mobile
}

export interface ExecuteAcrossClusterResult {
  parentExecutionId: string;
  childExecutionIds: string[];
  successCount: number;
  failedCount: number;
}

// ============================================================================
// FLOW ID MAPPING
// ============================================================================

/**
 * Map test type to system flow ID
 */
function getFlowIdForTestType(testType: QuickTestType): string {
  switch (testType) {
    case 'network':
      return 'sys_quick_network_test_flow';
    case 'sms':
      return 'sys_quick_sms_test_flow';
    case 'data':
      return 'sys_quick_data_test_flow';
    case 'data_performance':
      return 'sys_quick_data_performance_test_flow';
    case 'iot_mqtt':
      return 'sys_quick_iot_mqtt_test_flow';
    case 'iperf':
      return 'sys_quick_iperf_test_flow';
    case 'speedtest':
      return 'sys_quick_speedtest_test_flow';
    case 'voice_call':
      return 'sys_quick_voice_call_test_flow';
    case 'voice_agent':
      return 'sys_quick_voice_agent_test_flow';
    case 'test_like_mobile':
      return 'test_like_mobile'; // Special case - not a system flow
    default:
      throw new Error(`Unknown test type: ${testType}`);
  }
}

// ============================================================================
// RESOURCE ALLOCATION
// ============================================================================

/**
 * Get available eSIM profiles from a group
 */
export async function getAvailableESimsFromGroup(
  groupId: string,
  organizationId: string
): Promise<ESimProfile[]> {
  const q = query(
    collection(db, 'esim_profiles'),
    where('organizationId', '==', organizationId),
    where('groupId', '==', groupId),
    where('status', '==', 'available'),
    orderBy('createdAt', 'asc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as ESimProfile[];
}

/**
 * Get online edge devices at a location
 */
async function getOnlineDevicesAtLocation(locationId: string): Promise<CentralEdgeDevice[]> {
  const allDevices = await edgeDeviceService.getDevices();

  return allDevices.filter(device =>
    device.locationId === locationId &&
    device.status === 'online'
  );
}

/**
 * Select resources for quick test execution.
 * Only selects eSIM + location — modem allocation is the backend's job
 * (GlobalResourceManager handles scoring, registration checks, capabilities).
 */
export async function selectResources(
  eSimGroupId: string,
  locationIds: string[],
  organizationId: string
): Promise<QuickTestResources> {
  if (locationIds.length === 0) {
    throw new Error('No locations selected');
  }

  // Prefer an 'available' (not-yet-downloaded) member, but fall back to ANY
  // member of the group — including already-installed 'downloaded' single-use
  // profiles. Without this, a group whose codes have each been used once
  // dead-ends with "No available eSIM profiles" even though the profiles are
  // installed and reusable. The backend reuses the member already on the
  // allocated modem; TLAM (which runs on the modem's enabled profile) just
  // needs a valid handle.
  let esims = await getAvailableESimsFromGroup(eSimGroupId, organizationId);
  if (esims.length === 0) {
    const snap = await getDocs(query(
      collection(db, 'esim_profiles'),
      where('organizationId', '==', organizationId),
      where('groupId', '==', eSimGroupId),
      orderBy('createdAt', 'asc'),
    ));
    esims = snap.docs.map((d) => ({ id: d.id, ...d.data() })) as ESimProfile[];
  }
  if (esims.length === 0) {
    throw new Error('No eSIM profiles in selected group');
  }

  return {
    eSimProfileId: esims[0].id!,
    locationId: locationIds[0],
  };
}

/**
 * Select resources for Test Like A Mobile. Modem/device selection is done
 * server-side by `POST /tlam/sessions` (same `QueueService.findFreeModem`
 * path scheduled flows use), so we only need to surface the eSIM profile
 * and location here.
 */
export async function selectFirstAvailableResources(
  eSimGroupId: string,
  locationIds: string[],
  organizationId: string
): Promise<AutoStartConfig | null> {
  const resources = await selectResources(eSimGroupId, locationIds, organizationId);
  return {
    eSimProfileId: resources.eSimProfileId,
    locationId: resources.locationId,
    deviceId: '',
    modemImei: '',
  };
}

/**
 * Check if resources are available before execution
 */
export async function checkResourceAvailability(
  locationIds: string[],
  eSimGroupId: string,
  organizationId: string
): Promise<{
  available: boolean;
  message?: string;
  availableModems: number;
  availableEsims: number;
}> {
  // Check eSIM availability
  const availableEsims = await getAvailableESimsFromGroup(eSimGroupId, organizationId);

  // Check modem availability across all locations
  let availableModemCount = 0;
  for (const locationId of locationIds) {
    const onlineDevices = await getOnlineDevicesAtLocation(locationId);
    for (const device of onlineDevices) {
      const modems = await edgeDeviceService.getModems(device.id);
      availableModemCount += modems.length;
    }
  }

  if (availableEsims.length === 0) {
    return {
      available: false,
      message: 'No available eSIM profiles in selected group',
      availableModems: availableModemCount,
      availableEsims: 0,
    };
  }

  if (availableModemCount === 0) {
    return {
      available: false,
      message: 'No online modems available at selected locations',
      availableModems: 0,
      availableEsims: availableEsims.length,
    };
  }

  return {
    available: true,
    availableModems: availableModemCount,
    availableEsims: availableEsims.length,
  };
}

// ============================================================================
// EXECUTION
// ============================================================================

/**
 * Execute a quick test once with first available resources
 */
export async function executeOnce(
  testType: QuickTestType,
  locationIds: string[],
  eSimGroupId: string,
  organizationId: string,
  userId: string,
  inputs: Record<string, any> = {}
): Promise<ExecuteOnceResult> {
  // Special case: Test Like A Mobile
  if (testType === 'test_like_mobile') {
    // Select resources and return config for modal auto-start
    const autoStartConfig = await selectFirstAvailableResources(
      eSimGroupId,
      locationIds,
      organizationId
    );

    if (!autoStartConfig) {
      throw new Error('Failed to allocate resources for Test Like A Mobile');
    }

    return {
      executionId: '', // Not used for Test Like A Mobile
      autoStartConfig,
    };
  }

  // For other tests: Execute via runtime — backend allocates modem
  const flowId = getFlowIdForTestType(testType);

  const flow = await getFlow(flowId, organizationId);
  if (!flow) {
    throw new Error(`System flow not found: ${flowId}`);
  }

  const resources = await selectResources(eSimGroupId, locationIds, organizationId);

  const result = await executeFlow(
    flow,
    inputs,
    userId,
    organizationId,
    { eSimProfileId: resources.eSimProfileId },
    resources.locationId,
    false
  );

  return {
    executionId: result.executionId,
  };
}

/**
 * Execute a quick test across all locations in cluster (parallel execution)
 *
 * Creates a parent execution and executes child flows in parallel for each location.
 * Each child execution gets its own modem and eSIM allocation.
 */
export async function executeAcrossCluster(
  testType: QuickTestType,
  locationIds: string[],
  eSimGroupId: string,
  organizationId: string,
  userId: string,
  inputs: Record<string, any> = {}
): Promise<ExecuteAcrossClusterResult> {
  // Special case: Test Like A Mobile doesn't support cluster execution
  if (testType === 'test_like_mobile') {
    throw new Error('Test Like A Mobile does not support cluster execution');
  }

  if (locationIds.length === 0) {
    throw new Error('No locations provided for cluster execution');
  }

  // Get flow definition
  const flowId = getFlowIdForTestType(testType);
  const flow = await getFlow(flowId, organizationId);
  if (!flow) {
    throw new Error(`System flow not found: ${flowId}`);
  }

  // Get available eSIMs from group
  const availableEsims = await getAvailableESimsFromGroup(eSimGroupId, organizationId);
  if (availableEsims.length === 0) {
    throw new Error('No available eSIM profiles in selected group');
  }

  // Execute flow at each location in parallel — backend allocates modems
  const childExecutionPromises: Promise<{ executionId: string; locationId: string }>[] = [];
  const errors: string[] = [];

  for (let i = 0; i < locationIds.length; i++) {
    const locationId = locationIds[i];

    try {
      // Round-robin eSIM allocation
      const esimIndex = i % availableEsims.length;
      const esim = availableEsims[esimIndex];

      // Execute flow — backend GlobalResourceManager allocates modem at the location
      const executionPromise = executeFlow(
        flow,
        inputs,
        userId,
        organizationId,
        { eSimProfileId: esim.id! },
        locationId,
        false
      ).then(result => ({
        executionId: result.executionId,
        locationId,
      }));

      childExecutionPromises.push(executionPromise);
    } catch (error: any) {
      errors.push(`Failed to start execution for location ${locationId}: ${error.message}`);
    }
  }

  // Wait for all executions to start
  const childExecutions = await Promise.allSettled(childExecutionPromises);

  // Separate successful and failed executions
  const successfulExecutions = childExecutions
    .filter((result): result is PromiseFulfilledResult<{ executionId: string; locationId: string }> =>
      result.status === 'fulfilled'
    )
    .map(result => result.value);

  const failedExecutions = childExecutions
    .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
    .map(result => result.reason);

  // Add failed execution errors to errors array
  failedExecutions.forEach(error => {
    errors.push(error.message || 'Unknown error');
  });

  // If no executions succeeded, throw error
  if (successfulExecutions.length === 0) {
    throw new Error(`Failed to execute at any location. Errors: ${errors.join(', ')}`);
  }

  // Create a parent execution record
  // Note: For now, we'll use the first execution ID as the "parent"
  // In a full implementation, we would create a dedicated parent execution record
  const parentExecutionId = successfulExecutions[0].executionId;
  const childExecutionIds = successfulExecutions.slice(1).map(e => e.executionId);

  return {
    parentExecutionId,
    childExecutionIds,
    successCount: successfulExecutions.length,
    failedCount: failedExecutions.length,
  };
}
